/**
 * List of features that you can turn on | off in the application.
 *
 * The user will be allowed the feature if it is included in any applicable
 * plan or override.
 */
/**
 * The BooleanFeature enum is used by the internal API to define feature types,
 * and it is consumed by the platform to determine available subscription features.
 * Whenever a new feature is added and the platform needs to use it,
 * the internal-api-client package must be updated so the platform can receive the latest types.
 * If the newly added feature is not required by the platform yet, you only need to commit the updated schema-api.gql file
 */

export enum BooleanFeature {
  /** Being able to access items which have been shared with you.
   * For now covers items shared via the AdvancedShareControls, but will also
   * include shared templates */
  AccessSharedItems = "AccessSharedItems",
  AdvancedShareControls = "AdvancedShareControls",
  /** demographics, filtering, segmentation, cross-tab */
  Analytics = "Analytics",
  /** Includes Webhooks */
  Api = "Api",
  /*Slack authenticated Web Links - supposed to be an Enterprise only feature*/
  AuthenticatedWebLinks = "AuthenticatedWebLinks",
  DisableExportsOnMobile = "DisableExportsOnMobile",
  DisableGuestWebLinks = "DisableGuestWebLinks",
  EnterpriseActivityLogs = "EnterpriseActivityLogs",
  EnterpriseAdminDashboard = "EnterpriseAdminDashboard",
  EnterpriseApiTokensManagement = "EnterpriseApiTokensManagement",
  /** Customizations for the authoring experience -- currently only available for enterprise customers */
  EnterpriseCustomAuthoring = "EnterpriseCustomAuthoring",
  EnterpriseReporting = "EnterpriseReporting",
  EnterpriseSettingsDataRetention = "EnterpriseSettingsDataRetention",
  EnterpriseUserManagement = "EnterpriseUserManagement",
  EnterpriseLegacyBranding = "EnterpriseLegacyBranding",
  /** Controls whether marketing message logs are visible in activity logs */
  EnterpriseMarketingMessageLogs = "EnterpriseMarketingMessageLogs",
  ExportQuestion = "ExportQuestion",
  EnterpriseIPWhitelistWebExports = "EnterpriseIPWhitelistWebExports",
  EnterpriseUsagePage = "EnterpriseUsagePage",
  EnterpriseUsagePageHideAvgMAU = "EnterpriseUsagePageHideAvgMAU",
  EnterpriseUsagePageHidePeakMAU = "EnterpriseUsagePageHidePeakMAU",
  /** Allows connecting a google sheet to a Polly object and syncronizing data
   * between them */
  GoogleSheetsSync = "GoogleSheetsSync",
  /** User is on a centrally managed trial */
  ManagedTrialLicense = "ManagedTrialLicense",
  /** Just a marker for non-free subscriptions */
  PaidTier = "PaidTier",
  /** User can create official pollys */
  OfficialMessage = "OfficialMessage",
  /** User sees Request License button when feature not permitted by free */
  RequestManagedTrialLicense = "RequestManagedTrialLicense",
  SlashPollyExport = "SlashPollyExport",
  SlashPollyShare = "SlashPollyShare",
  ManageSuggestionBox = "ManageSuggestionBox",
  TrendHistory = "TrendHistory",
  Themes = "Themes",
  Workflows = "Workflows",
  QnAv2 = "QnAv2",
  DisableLLM = "DisableLLM",

  /** Enterprise crackdown flags */
  DisableAnonymity = "DisableAnonymity",
  DisablePrivateResults = "DisablePrivateResults",
  DisableSchedulingOptions = "DisableSchedulingOptions",
  OrgMonthlyResponseLimitEnabled = "OrgMonthlyResponseLimitEnabled",
  DisableResponseLimitCreationBlocking = "DisableResponseLimitCreationBlocking",
  EnableResultsFiltering = "EnableResultsFiltering",

  /** Addiitional flags added to accommodate pricing 2025 changes */
  AddAudienceByEmailList = "AddAudienceByEmailList",
  DisableAdvanceQnA = "DisableAdvanceQnA",
  DisablePremiumPolly = "DisablePremiumPolly",
  DisableTemplateSharing = "DisableTemplateSharing",
  DisableResultsCollaborators = "DisableResultsCollaborators",
  HideResponseLimits = "HideResponseLimits",
  ImagePolly = "ImagePolly",
  Quiz = "Quiz",
  WordCloud = "WordCloud",

  /** Org-level kill switch for QR codes across all Polly types and surfaces.
   * When true, QR rendering is hidden in authoring, details, and presentation,
   * and the API rejects attempts to set sharingSettings.qrCode != "none".
   * Originally added for McKinsey (Orca) tenant. */
  DisableQRCode = "DisableQRCode",

  /** Controls access to enterprise marketing settings UI */
  EnterpriseMarketingSettings = "EnterpriseMarketingSettings",

  SlackUserGroupsCollaboration = "SlackUserGroupsCollaboration",

  /** Enables the Polly Pairing (Coffee Talk) tab on the new App Home layout. */
  PollyPairing = "PollyPairing",

  SlackMCP = "SlackMCP",

  EventsGA = "EventsGA",

  /** Lets hosted-event organizers enable email (magic-code) audience
   * authentication on authenticated events (PS-5458). */
  EventsAudienceEmailAuth = "EventsAudienceEmailAuth",
}
/**
 * List of features that have numeric max values associated with them.
 *
 * The user will receive the max of the default value and values from any
 * applicable plans and overrides.
 */
export enum NumericFeature {
  DaysOfHistory = "DaysOfHistory",
  DirectMessageAudienceLimit = "DirectMessageAudienceLimit",
  /** Responses Per Month Used for Response Based Paywall */
  ResponsesPerMonth = "ResponsesPerMonth",
  /** Enterprise Crackdown */
  OrgMonthlyResponseLimit = "OrgMonthlyResponseLimit",
}

/**
 * For each NumericFeature a default value must be specified.
 * This is the quantity included with no subscribed plan.
 */
export const NumericFeatureDefaultValues: {
  [key in NumericFeature]: number;
} = {
  [NumericFeature.DirectMessageAudienceLimit]: 1,
  [NumericFeature.OrgMonthlyResponseLimit]: 0,
  [NumericFeature.DaysOfHistory]: 45,
  [NumericFeature.ResponsesPerMonth]: 25,
};

/**
 * For each feature an optional name may be specified for use in UI.
 *
 * If no name is specified here, the enum string will be used.
 */
export const UserFriendlyFeatureNames: {
  [key in BooleanFeature]?: string;
} & {
  [key in NumericFeature]?: string;
} = {
  [BooleanFeature.AccessSharedItems]: "Shared Items",
  [BooleanFeature.AdvancedShareControls]: "Advanced Share Controls",
  [BooleanFeature.Api]: "APIs",
  [BooleanFeature.AuthenticatedWebLinks]: "Authenticated Web Voting",
  [BooleanFeature.EnterpriseActivityLogs]: "Activity Logs",
  [BooleanFeature.EnterpriseAdminDashboard]: "Enterprise Admin Dashboards",
  [BooleanFeature.EnterpriseApiTokensManagement]: "API Tokens Management",
  [BooleanFeature.EnterpriseCustomAuthoring]: "Enterprise Custom Authoring",
  [BooleanFeature.EnterpriseReporting]: "Enterprise Reports",
  [BooleanFeature.EnterpriseSettingsDataRetention]: "Enterprise Settings",
  [BooleanFeature.EnterpriseUserManagement]: "User Management Tools",
  [BooleanFeature.ExportQuestion]: "Exports",
  [BooleanFeature.ManagedTrialLicense]: "Managed Trial License",
  [BooleanFeature.PaidTier]: "Paid Tier",
  [BooleanFeature.RequestManagedTrialLicense]: "Request Managed Trial License",
  [BooleanFeature.SlashPollyExport]: "Exports",
  [BooleanFeature.SlashPollyShare]: "Shares",
  [BooleanFeature.TrendHistory]: "Trend History",
};
